import { Type } from "@sinclair/typebox";
// ─── Shared Cell Types ───────────────────────────────────────────────────────
export const CellStyleSchema = Type.Object({
    // Keep legacy names for compatibility
    fontWeight: Type.Optional(Type.Union([Type.Literal("normal"), Type.Literal("bold")])),
    fontStyle: Type.Optional(Type.Union([Type.Literal("normal"), Type.Literal("italic")])),
    fontLine: Type.Optional(Type.Union([Type.Literal("none"), Type.Literal("underline"), Type.Literal("line-through")])),
    fontSize: Type.Optional(Type.Number()),
    fontFamily: Type.Optional(Type.String()),
    fontColor: Type.Optional(Type.String()),
    backgroundColor: Type.Optional(Type.String()),
    horizontalAlignment: Type.Optional(Type.Union([Type.Literal("left"), Type.Literal("center"), Type.Literal("right")])),
    numberFormat: Type.Optional(Type.String()),
    // New, direct names from RangeStyle to reduce mapping friction
    bold: Type.Optional(Type.Boolean()),
    italic: Type.Optional(Type.Boolean()),
    color: Type.Optional(Type.String()), // maps to fontColor
    underline: Type.Optional(Type.Union([
        Type.Literal("None"),
        Type.Literal("Single"),
        Type.Literal("Double"),
        Type.Literal("SingleAccountant"),
        Type.Literal("DoubleAccountant"),
    ])),
});
export const BorderSideSchema = Type.Object({
    style: Type.Optional(Type.String()),
    weight: Type.Optional(Type.String()),
    color: Type.Optional(Type.String()),
});
export const BorderStyleSchema = Type.Object({
    top: Type.Optional(BorderSideSchema),
    bottom: Type.Optional(BorderSideSchema),
    left: Type.Optional(BorderSideSchema),
    right: Type.Optional(BorderSideSchema),
});
export const CellInputSchema = Type.Object({
    value: Type.Optional(Type.Unknown()),
    formula: Type.Optional(Type.String()),
    note: Type.Optional(Type.String()),
    cellStyles: Type.Optional(CellStyleSchema),
    borderStyles: Type.Optional(BorderStyleSchema),
});
// ─── Tool Parameter Schemas ──────────────────────────────────────────────────
export const GetRangeAsJsonParamsSchema = Type.Object({
    sheetId: Type.Number({ description: "The worksheet ID (1-based index)" }),
    ranges: Type.Array(Type.String(), { description: "Array of ranges in A1 notation, e.g. ['A1:C10', 'E1:F5']" }),
    includeStyles: Type.Optional(Type.Boolean({ default: false })),
    cellLimit: Type.Optional(Type.Number({ default: 2000 })),
    workbookId: Type.Optional(Type.String({ description: "Target a specific connected workbook by ID." })),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const GetRangeAsCsvParamsSchema = Type.Object({
    sheetId: Type.Number({ description: "The worksheet ID (1-based index)" }),
    range: Type.String({ description: "A1 range, e.g. 'A1:Z100'" }),
    includeHeaders: Type.Optional(Type.Boolean({ default: true })),
    maxRows: Type.Optional(Type.Number({ default: 400 })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const SearchDataParamsSchema = Type.Object({
    sheetId: Type.Optional(Type.Number()),
    searchTerm: Type.String(),
    maxResults: Type.Optional(Type.Number({ default: 20 })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const SetCellRangeParamsSchema = Type.Object({
    sheetId: Type.Number(),
    range: Type.String(),
    cells: Type.Array(Type.Array(CellInputSchema)),
    allowOverwrite: Type.Optional(Type.Boolean({
        default: false,
        description: "If false or omitted, refuse writes that would overwrite non-empty cells; retry with true only after user confirms overwrite.",
    })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const ClearCellRangeParamsSchema = Type.Object({
    sheetId: Type.Number(),
    range: Type.String(),
    clearType: Type.Optional(Type.Union([
        Type.Literal("contents"),
        Type.Literal("all"),
        Type.Literal("formats"),
    ])),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const CopyToParamsSchema = Type.Object({
    sheetId: Type.Number(),
    sourceRange: Type.String(),
    destinationRange: Type.String(),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const ModifySheetStructureParamsSchema = Type.Object({
    sheetId: Type.Number(),
    operation: Type.Union([
        Type.Literal("insert"),
        Type.Literal("delete"),
        Type.Literal("hide"),
        Type.Literal("unhide"),
        Type.Literal("freeze"),
        Type.Literal("unfreeze"),
    ]),
    dimension: Type.Union([Type.Literal("rows"), Type.Literal("columns")]),
    reference: Type.Optional(Type.String()),
    count: Type.Optional(Type.Number()),
    position: Type.Optional(Type.Union([Type.Literal("before"), Type.Literal("after")])),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const ModifyWorkbookStructureParamsSchema = Type.Object({
    operation: Type.Union([
        Type.Literal("create"),
        Type.Literal("delete"),
        Type.Literal("rename"),
        Type.Literal("duplicate"),
    ]),
    sheetId: Type.Optional(Type.Number()),
    sheetName: Type.Optional(Type.String()),
    newName: Type.Optional(Type.String()),
    tabColor: Type.Optional(Type.String()),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const ResizeRangeParamsSchema = Type.Object({
    sheetId: Type.Number(),
    range: Type.Optional(Type.String()),
    width: Type.Optional(Type.Object({
        type: Type.Union([Type.Literal("points"), Type.Literal("standard")]),
        value: Type.Number(),
    })),
    height: Type.Optional(Type.Object({
        type: Type.Union([Type.Literal("points"), Type.Literal("standard")]),
        value: Type.Number(),
    })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
const PivotFieldSchema = Type.Object({
    field: Type.String(),
    summarizeBy: Type.Optional(Type.Union([
        Type.Literal("sum"), Type.Literal("count"),
        Type.Literal("average"), Type.Literal("max"), Type.Literal("min"),
    ])),
});
export const ModifyObjectParamsSchema = Type.Object({
    operation: Type.Union([Type.Literal("create"), Type.Literal("update"), Type.Literal("delete")]),
    sheetId: Type.Number(),
    objectType: Type.Union([Type.Literal("pivotTable"), Type.Literal("chart")]),
    id: Type.Optional(Type.String()),
    properties: Type.Optional(Type.Object({
        name: Type.Optional(Type.String()),
        source: Type.Optional(Type.String({ description: "Data source range, e.g. 'Sheet1!A1:D100'" })),
        range: Type.Optional(Type.String()),
        anchor: Type.Optional(Type.String()),
        rows: Type.Optional(Type.Array(Type.Object({ field: Type.String() }))),
        columns: Type.Optional(Type.Array(Type.Object({ field: Type.String() }))),
        values: Type.Optional(Type.Array(PivotFieldSchema)),
        title: Type.Optional(Type.String()),
        chartType: Type.Optional(Type.Union([
            Type.Literal("columnClustered"), Type.Literal("barClustered"),
            Type.Literal("line"), Type.Literal("pie"), Type.Literal("scatter"),
            Type.Literal("area"), Type.Literal("doughnut"),
        ])),
    })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const GetAllObjectsParamsSchema = Type.Object({
    sheetId: Type.Optional(Type.Number()),
    id: Type.Optional(Type.String()),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const EvalOfficeJsParamsSchema = Type.Object({
    code: Type.String({ description: "JavaScript code to execute inside Excel.run context." }),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 100 })),
});
export const ScreenshotRangeParamsSchema = Type.Object({
    sheetId: Type.Number(),
    range: Type.String(),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 50 })),
});
export const AnalyzeSheetStructureParamsSchema = Type.Object({
    sheetId: Type.Optional(Type.Number({ description: "Optional: Worksheet ID (1-based). Defaults to active sheet." })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 100 })),
});
export const ImportDataParamsSchema = Type.Object({
    sheetName: Type.Optional(Type.String({ description: "Optional: The name for a new worksheet. Only used if sheetId is not provided." })),
    sheetId: Type.Optional(Type.Number({ description: "Optional: Target an existing worksheet by ID. If provided, data will be written starting at A1." })),
    data: Type.Array(Type.Array(Type.Any()), { description: "2D array of data (rows and columns) to import." }),
    hasHeaders: Type.Optional(Type.Boolean({ default: true, description: "Whether the first row contains headers to be formatted." })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 100 })),
});
export const InsertBatchParamsSchema = Type.Object({
    attachmentId: Type.String({ description: "Attachment ID with structured_data available." }),
    sheetName: Type.Optional(Type.String({ description: "Optional: The name for a new worksheet. Only used if sheetId is not provided." })),
    sheetId: Type.Optional(Type.Number({ description: "Optional: Target an existing worksheet by ID. If provided, data will be written starting at A1." })),
    hasHeaders: Type.Optional(Type.Boolean({ default: true, description: "Whether the first row contains headers to be formatted." })),
    tableIndex: Type.Optional(Type.Number({ default: 0, description: "Which table from the attachment to import." })),
    workbookId: Type.Optional(Type.String()),
    explanation: Type.Optional(Type.String({ maxLength: 100 })),
});
// ─── Tool Name Registry ─────────────────────────────────────────────────────
export const TOOL_NAMES = {
    GET_RANGE_AS_JSON: "get_range_as_json",
    GET_RANGE_AS_CSV: "get_range_as_csv",
    SEARCH_DATA: "search_data",
    GET_ALL_OBJECTS: "get_all_objects",
    SCREENSHOT_RANGE: "screenshot_range",
    SET_CELL_RANGE: "set_cell_range",
    CLEAR_CELL_RANGE: "clear_cell_range",
    COPY_TO: "copy_to",
    MODIFY_SHEET_STRUCTURE: "modify_sheet_structure",
    MODIFY_WORKBOOK_STRUCTURE: "modify_workbook_structure",
    RESIZE_RANGE: "resize_range",
    MODIFY_OBJECT: "modify_object",
    EVAL_OFFICEJS: "eval_officejs",
    ANALYZE_SHEET_STRUCTURE: "analyze_sheet_structure",
    IMPORT_DATA: "import_data",
    INSERT_BATCH: "insert_batch",
    SUMMARIZE_WORKBOOK: "summarize_workbook",
    WRITE_CELLS: "write_cells",
    BASH: "bash",
    READ: "read",
    PYTHON_EXEC: "python_exec",
    ASK_USER: "ask_user",
};
//# sourceMappingURL=schemas.js.map